import React, { useEffect, useState } from 'react';
import { Form, Input, Button, DatePicker, Select, Spin, message } from 'antd';
import { Link } from 'react-router-dom';
import { getAllStates } from "../../services/statesApi";
import { signUpRequest } from '../../services/authApi';
import { getRoles } from '../../services/adminApi';
import { State } from '../../@types/state';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

const { Option } = Select;
dayjs.locale('ru');

export const SignUp: React.FC = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [states, setStates] = useState<State[]>([]);
    const [roles, setRoles] = useState<Array<{ id: number, name: string }>>([]);
    const [state, setState] = useState<number | null>(null);
    const [phonePrefix, setPhonePrefix] = useState<string>('+7');
    const [cityError, setCityError] = useState(false);

    const [form] = Form.useForm();

    const validateEmail = (email: string) => {
        const russianLetters = /[а-яё]/i;
        if (russianLetters.test(email)) {
            return Promise.reject(new Error('Email не должен содержать русские буквы!'));
        }
        return Promise.resolve();
    };

    const handlePrefixChange = (value: string) => {
        setPhonePrefix(value);
        form.setFieldValue('phone', '');
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const digitsOnly = value.replace(/\D/g, '');
        
        if (phonePrefix === '+7' && digitsOnly.length > 10) {
            const limitedDigits = digitsOnly.substring(0, 10);
            form.setFieldValue('phone', limitedDigits);
        } else if (phonePrefix === '+3' && digitsOnly.length > 11) {
            const limitedDigits = digitsOnly.substring(0, 11);
            form.setFieldValue('phone', limitedDigits);
        }
    };

    const validatePhone = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(new Error('Пожалуйста, введите номер телефона!'));
        }
        
        const digitsOnly = value.replace(/\D/g, '');
        
        if (phonePrefix === '+3') {
            if (digitsOnly.length !== 11) {
                return Promise.reject(new Error('Номер телефона должен содержать 11 цифр после кода +3!'));
            }
        } else if (phonePrefix === '+7') {
            if (digitsOnly.length !== 10) {
                return Promise.reject(new Error('Российский номер телефона должен содержать 10 цифр после кода страны!'));
            }
        }
        
        return Promise.resolve();
    };

    const handlePhoneInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (allowedKeys.includes(e.key)) {
            return;
        }

        if (!/[0-9]/.test(e.key)) {
            e.preventDefault();
            return;
        }

        const currentValue = (e.target as HTMLInputElement).value;
        const digitsOnly = currentValue.replace(/\D/g, '');
        
        if (phonePrefix === '+7' && digitsOnly.length >= 10) {
            e.preventDefault();
        } else if (phonePrefix === '+3' && digitsOnly.length >= 11) {
            e.preventDefault();
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const [statesResult, rolesResult] = await Promise.all([
                    getAllStates('F'),
                    getRoles()
                ]);
                setStates(statesResult);
                const allowedRoleIds = [4, 5, 6, 9, 10, 11, 12];
                const filteredRoles = rolesResult.filter((role: any) => allowedRoleIds.includes(role.id));
                setRoles([{ id: 0, name: 'Продавец' }, ...filteredRoles]);
            } catch (err) {
                message.error('Не удалось загрузить данные. Пожалуйста, попробуйте позже или обратитесь к администратору.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const onFinish = async (values: any) => {
        if (!values.city || !values.city.trim()) {
            setCityError(true);
            return;
        }
        setCityError(false);
        setIsLoading(true);
        try {
            const { firstName, lastName, middleName, birthDate, email, phone, password, storeName, city, storeAddress, role } = values;
            const selectedState = states.find(item => item.id === state);

            const fullPhone = phonePrefix + phone.replace(/\D/g, '');

            const response = await signUpRequest({
                firstName,
                lastName,
                middleName,
                birthDate,
                email,
                phone: fullPhone,
                password,
                stateId: state || 0,
                stateName: selectedState ? selectedState.name : '',
                storeName,
                city,
                storeAddress,
                role,
            });

            if (response?.user) {
                message.success({ content: 'Пользователь успешно создан.', duration: 10 });
                window.location.replace('/login');
            }
        } catch (err: any) {
            if (err?.message) {
                message.error(err.message);
            } else {
                message.error('Произошла ошибка. Попробуйте снова.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: 600, margin: '20px auto', padding: 20 }}>
            <h1 style={{ textAlign: 'center' }}>Регистрация</h1>

            <Spin spinning={isLoading}>
                <Form form={form} onFinish={onFinish} layout="vertical" initialValues={{ remember: true }}>
                    <Form.Item label="Фамилия" name="lastName" rules={[{ required: true, message: 'Пожалуйста, введите фамилию!' }]}>
                        <Input placeholder="Введите фамилию" />
                    </Form.Item>

                    <Form.Item label="Имя" name="firstName" rules={[{ required: true, message: 'Пожалуйста, введите имя!' }]}>
                        <Input placeholder="Введите имя" />
                    </Form.Item>

                    <Form.Item label="Отчество" name="middleName" rules={[{ required: true, message: 'Пожалуйста, введите отчество!' }]}>
                        <Input placeholder="Введите отчество" />
                    </Form.Item>

                    <Form.Item label="Дата рождения" name="birthDate" rules={[{ required: true, message: 'Пожалуйста, выберите дату рождения!' }]}>
                        <DatePicker 
                            style={{ width: '100%' }} 
                            placeholder="Выберите дату рождения" 
                            format="DD.MM.YYYY"
                        />
                    </Form.Item>

                    <Form.Item 
                        label="Электронная почта" 
                        name="email" 
                        rules={[
                            { required: true, message: 'Пожалуйста, введите email!' },
                            { type: 'email', message: 'Пожалуйста, введите корректный email!' },
                            { validator: (_, value) => validateEmail(value) }
                        ]}
                    >
                        <Input placeholder="Введите почту" />
                    </Form.Item>

                    <Form.Item
                        label="Номер телефона"
                        name="phone"
                        rules={[
                            { validator: validatePhone }
                        ]}
                    >
                        <Input 
                            addonBefore={
                                <Select
                                    value={phonePrefix}
                                    size="small"
                                    onChange={handlePrefixChange}
                                    style={{ width: 70 }}
                                >
                                    <Option value="+7">+7</Option>
                                    <Option value="+3">+3</Option>
                                </Select>
                            }
                            placeholder={phonePrefix === '+7' ? '9991234567' : '75291234567'}
                            onKeyDown={handlePhoneInput}
                            onChange={handlePhoneChange}
                            maxLength={phonePrefix === '+7' ? 10 : 11}
                        />
                    </Form.Item>

                    <Form.Item label="Пароль" name="password" rules={[{ required: true, message: 'Пожалуйста, введите пароль!' }, { min: 8, message: 'Пароль должен быть минимум 8 символов!' }]}>
                        <Input.Password placeholder="Введите пароль" />
                    </Form.Item>

                    <Form.Item
                        label="Повторите пароль"
                        name="confirmPassword"
                        dependencies={['password']}
                        rules={[
                            { required: true, message: 'Пожалуйста, повторите пароль!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Введенные пароли не совпадают!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="Повторите пароль" />
                    </Form.Item>

                    <Form.Item label="Подразделение" name="stateId" rules={[{ required: true, message: 'Пожалуйста, выберите подразделение!' }]}>
                        <Select
                            placeholder="Выберите подразделение"
                            style={{ marginTop: 7, width: '100%' }}
                            options={states.map((x) => ({ value: x.id, label: x.name }))}
                            onChange={setState}
                            allowClear
                        />
                    </Form.Item>

                    <Form.Item label="Должность" name="role" rules={[{ required: true, message: 'Пожалуйста, выберите должность!' }]}>
                        <Select
                            placeholder="Выберите должность"
                            style={{ width: '100%' }}
                            options={roles.map((role) => ({ value: role.id, label: role.name }))}
                            allowClear
                        />
                    </Form.Item>

                    <Form.Item label="Название вашего магазина" name="storeName">
                        <Input placeholder="Введите название магазина" />
                    </Form.Item>

                    <Form.Item label="Город" name="city" rules={[{ required: true, message: 'Пожалуйста, введите город!' }, { validator: (_, value) => value && value.trim() ? Promise.resolve() : Promise.reject('Город не может состоять только из пробелов!') }]}>
                        <Input placeholder="Введите город" onChange={e => setCityError(!e.target.value.trim())} />
                    </Form.Item>

                    <Form.Item label="Адрес магазина" name="storeAddress">
                        <Input.TextArea placeholder="Введите адрес магазина" />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block disabled={cityError || isLoading}>
                            Зарегистрироваться
                        </Button>
                    </Form.Item>

                    <div style={{ textAlign: 'center' }}>
                        Уже есть аккаунт? <Link to="/login">Войти</Link>
                    </div>
                </Form>
            </Spin>
        </div>
    );
};

export default SignUp;
